import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from './useAuth';

const UPGRADE_REFRESH_KEY = 'creately:upgrade-entitlement-refresh';
const UPGRADE_REFRESH_TTL_MS = 30 * 60 * 1000;

type EntitlementsRefreshResponse = {
  success?: boolean;
  data?: { planType?: string } & Record<string, unknown>;
};

function hasPendingRefresh(): boolean {
  try {
    const value = sessionStorage.getItem(UPGRADE_REFRESH_KEY);
    if (!value) return false;
    const createdAt = Number(value);
    if (!Number.isFinite(createdAt) || Date.now() - createdAt > UPGRADE_REFRESH_TTL_MS) {
      sessionStorage.removeItem(UPGRADE_REFRESH_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function clearPendingRefresh(): void {
  try {
    sessionStorage.removeItem(UPGRADE_REFRESH_KEY);
  } catch {
    // Storage may be unavailable; the refresh remains best effort.
  }
}

export function markUpgradeEntitlementRefreshPending(): void {
  try {
    sessionStorage.setItem(UPGRADE_REFRESH_KEY, String(Date.now()));
  } catch {
    // The upgrade still proceeds when storage is unavailable.
  }
}

/** Refreshes the authoritative plan after returning from the external checkout. */
export function useUpgradeEntitlementRefresh(): void {
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();
  const refreshInFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (refreshInFlight.current || !hasPendingRefresh()) return;
    refreshInFlight.current = true;
    try {
      const response = await authenticatedFetch('/api/me/entitlements?refresh=1');
      if (!response.ok) return;
      const payload = await response.json() as EntitlementsRefreshResponse;
      if (!payload.success || !payload.data) return;

      queryClient.setQueryData(['entitlements', null], payload.data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['me'] }),
        queryClient.invalidateQueries({ queryKey: ['entitlements'] }),
      ]);

      if (payload.data.planType && payload.data.planType !== 'free') {
        clearPendingRefresh();
      }
    } catch {
      // Checkout completion and the upstream profile update are asynchronous;
      // retain the marker so a later focus or mount can retry.
    } finally {
      refreshInFlight.current = false;
    }
  }, [authenticatedFetch, queryClient]);

  useEffect(() => {
    void refresh();
    const onFocus = () => void refresh();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refresh]);
}
